import { z } from 'zod';
import { lenientRequiredNode, lenientNodeArray } from '../schema-extensions';
import { UnitsNode } from './units-node-schema';
import { PetNode } from './pet-node-schema';
import { PersonNode } from './person-node-schema';
import { IncomeNode } from './income-node-schema';
import { ExpenditureNode } from './expenditure-node-schema';
import { VehicleNode } from './vehicle-node-schema';
import { PropertyNode } from './property-node-schema';
import { ItemNode } from './item-node-schema';
import { ProtectionNode } from './protection-node-schema';
import { LiabilityNode } from './liability-node-schema';
import { SavingsNode } from './savings-node-schema';
import { DocumentNode } from './document-node-schema';
import { VaultSettingsNode } from './vault-settings-node-schema';

// ============================================================================
// LENIENT SCHEMA (for deserialization)
// ============================================================================
const lenientVaultDocumentSchema = z.object({
  units: lenientRequiredNode(UnitsNode),
  people: lenientNodeArray(PersonNode.deserialize),
  pets: lenientNodeArray(PetNode.deserialize),
  incomes: lenientNodeArray(IncomeNode.deserialize),
  expenditures: lenientNodeArray(ExpenditureNode.deserialize),
  vehicles: lenientNodeArray(VehicleNode.deserialize),
  properties: lenientNodeArray(PropertyNode.deserialize),
  items: lenientNodeArray(ItemNode.deserialize),
  protection: lenientNodeArray(ProtectionNode.deserialize),
  liabilities: lenientNodeArray(LiabilityNode.deserialize),
  savings: lenientNodeArray(SavingsNode.deserialize),
  documents: lenientNodeArray(DocumentNode.deserialize),
  settings: z.unknown().optional().catch(undefined).transform(val => VaultSettingsNode.deserialize(val ?? {})),
}).transform(val => ({
  units: val.units,
  people: val.people,
  pets: val.pets,
  incomes: val.incomes,
  expenditures: val.expenditures,
  vehicles: val.vehicles,
  properties: val.properties,
  items: val.items,
  protection: val.protection,
  liabilities: val.liabilities,
  savings: val.savings,
  documents: val.documents,
  settings: val.settings,
}));

// ============================================================================
// STRICT SCHEMA (for validation)
// ============================================================================
const strictVaultDocumentSchema = z.object({
  units: z.unknown().transform(val => UnitsNode.create(val as any)),
  people: z.array(z.unknown()).min(0).transform(arr => arr.map(val => PersonNode.create(val as any))),
  pets: z.array(z.unknown()).min(0).transform(arr => arr.map(val => PetNode.create(val as any))),
  incomes: z.array(z.unknown()).min(0).transform(arr => arr.map(val => IncomeNode.create(val as any))),
  expenditures: z.array(z.unknown()).min(0).transform(arr => arr.map(val => ExpenditureNode.create(val as any))),
  vehicles: z.array(z.unknown()).min(0).transform(arr => arr.map(val => VehicleNode.create(val as any))),
  properties: z.array(z.unknown()).min(0).transform(arr => arr.map(val => PropertyNode.create(val as any))),
  items: z.array(z.unknown()).min(0).transform(arr => arr.map(val => ItemNode.create(val as any))),
  protection: z.array(z.unknown()).min(0).transform(arr => arr.map(val => ProtectionNode.create(val as any))),
  liabilities: z.array(z.unknown()).min(0).transform(arr => arr.map(val => LiabilityNode.create(val as any))),
  savings: z.array(z.unknown()).min(0).transform(arr => arr.map(val => SavingsNode.create(val as any))),
  documents: z.array(z.unknown()).min(0).transform(arr => arr.map(val => DocumentNode.create(val as any))),
  settings: z.unknown().optional().transform(val => VaultSettingsNode.create(val ?? {})),
}).transform(val => ({
  units: val.units,
  people: val.people,
  pets: val.pets,
  incomes: val.incomes,
  expenditures: val.expenditures,
  vehicles: val.vehicles,
  properties: val.properties,
  items: val.items,
  protection: val.protection,
  liabilities: val.liabilities,
  savings: val.savings,
  documents: val.documents,
  settings: val.settings,
}));

// ============================================================================
// TYPE
// ============================================================================
export type VaultDocument = z.infer<typeof lenientVaultDocumentSchema>;

// ============================================================================
// ACTIVE COUNTS
// ============================================================================
export interface ActiveCounts {
  people: number;
  pets: number;
  vehicles: number;
  properties: number;
  items: number;
  incomes: number;
  expenditures: number;
  savings: number;
  protection: number;
  liabilities: number;
}

// ============================================================================
// NAMESPACE (for Methods)
// ============================================================================
export namespace VaultDocument {
  /**
   * Deserialize and validate an entire vault document from unknown data
   * This handles all nested node deserialization automatically
   */
  export function deserialize(data: unknown): VaultDocument {
    return lenientVaultDocumentSchema.parse(data);
  }

  /**
   * Create and validate an entire vault document with strict validation
   */
  export function create(data: unknown): VaultDocument {
    return strictVaultDocumentSchema.parse(data);
  }

  /**
   * Create an empty vault document with default units
   */
  export function createEmpty(): VaultDocument {
    return {
      units: UnitsNode.createDefault(),
      settings: VaultSettingsNode.createDefault(),
      people: [],
      pets: [],
      incomes: [],
      expenditures: [],
      vehicles: [],
      properties: [],
      items: [],
      protection: [],
      liabilities: [],
      savings: [],
      documents: [],
    };
  }

  /**
   * Count the number of active resources in the vault document,
   * excluding archived people/pets, sold assets, expired protection,
   * repaid liabilities, and ended incomes/expenditures.
   */
  export function countActive(data: VaultDocument, referenceDate: Date = new Date()): ActiveCounts {
    return {
      people: data.people.filter(p => PersonNode.isActive(p, referenceDate)).length,
      pets: data.pets.filter(p => PetNode.isActive(p, referenceDate)).length,
      vehicles: data.vehicles.filter(v => VehicleNode.isActive(v)).length,
      properties: data.properties.filter(p => PropertyNode.isActive(p)).length,
      items: data.items.filter(i => ItemNode.isActive(i)).length,
      incomes: data.incomes.filter(i => IncomeNode.isActive(i, referenceDate)).length,
      expenditures: data.expenditures.filter(e => ExpenditureNode.isActive(e, referenceDate)).length,
      savings: data.savings.filter(s => SavingsNode.isActive(s, referenceDate)).length,
      protection: data.protection.filter(p => ProtectionNode.isActive(p, referenceDate)).length,
      liabilities: data.liabilities.filter(l => LiabilityNode.isActive(l, referenceDate)).length,
    };
  }
}
